import ChartLegend from '@app/components/charts/legends/chart-legend';
import SimpleBarChart from '@shared/components/chart/simple-bar-chart';
import { truncateLabel } from '@shared/components/chart/utils';
import { ChartCard } from '@shared/components/dashboard/chart-card';
import { DashboardGrid } from '@shared/components/dashboard/dashboard-grid';
import { DashboardSection } from '@shared/components/dashboard/dashboard-section';
import { MetricCard } from '@shared/components/dashboard/metric-card';
import { MetricStack } from '@shared/components/dashboard/metric-stack';
import { ChartConfig } from '@shared/components/ui/chart';
import { getCurrencySymbol } from '@shared/lib/format';
import { PurchasedGoodsSpend, TimePeriod } from '@shared/api/types';
import { useMemo } from 'react';

interface OverallSpendSectionProps {
  spend: PurchasedGoodsSpend;
  currency: string | null | undefined;
  monthlyPeriods: TimePeriod[];
  categoryLabel?: string;
}

const MONTH_YEAR = new Intl.DateTimeFormat('en-GB', {
  month: 'short',
  year: '2-digit',
});

function formatCompact(value: number, symbol: string): string {
  const abs = Math.abs(value);
  const trim = (n: number) => (n % 1 === 0 ? n.toFixed(0) : n.toFixed(1));
  if (abs >= 1_000_000) return `${symbol}${trim(value / 1_000_000)}m`;
  if (abs >= 1_000) return `${symbol}${trim(value / 1_000)}k`;
  return `${symbol}${value.toFixed(0)}`;
}

export function OverallSpendSection({
  spend,
  currency,
  monthlyPeriods,
  categoryLabel,
}: OverallSpendSectionProps) {
  const symbol = getCurrencySymbol(currency);

  // Render the currency symbol in the muted unit colour, spaced off the figure,
  // so it reads as a unit rather than blending into the number.
  const withCurrency = (formatted: string) =>
    symbol ? (
      <>
        <span className="mr-1 text-primary-500">{symbol}</span>
        {formatted}
      </>
    ) : (
      formatted
    );

  const totalSpend = spend.totalSpend != null ? Number(spend.totalSpend) : null;
  const averageMonthlySpend =
    spend.averageMonthlySpend != null
      ? Number(spend.averageMonthlySpend)
      : null;
  const overallSpend =
    spend.overallSpendAllPurchasedGoods != null
      ? Number(spend.overallSpendAllPurchasedGoods)
      : null;
  const proportion = spend.proportionOverallSpend;

  const periodLabel =
    spend.timePeriodStart && spend.timePeriodEnd
      ? `${MONTH_YEAR.format(new Date(spend.timePeriodStart.startDate))} - ${MONTH_YEAR.format(new Date(spend.timePeriodEnd.endDate))}`
      : '—';

  const chartData = useMemo(() => {
    const byUuid = new Map(
      spend.spendOverTime.map((month) => {
        const actual = Number(month.totalMonthSpend);
        const baselineProjection = Number(month.totalSpendFirstTimePeriod);
        return [
          month.timePeriodUuid,
          {
            baseline: Math.min(actual, baselineProjection),
            additional: Math.max(0, actual - baselineProjection),
          },
        ];
      })
    );
    return monthlyPeriods.map((period) => {
      const existing = byUuid.get(period.uuid);
      return {
        period: period.name,
        baseline: existing?.baseline ?? 0,
        additional: existing?.additional ?? 0,
      };
    });
  }, [spend.spendOverTime, monthlyPeriods]);

  const firstPeriodLabel =
    spend.spendOverTime.length > 0 ? spend.spendOverTime[0].timePeriodName : '';

  const chartConfig = {
    baseline: {
      label: firstPeriodLabel
        ? `Baseline (${firstPeriodLabel} price)`
        : 'Baseline',
      color: 'var(--spend-400)',
    },
    additional: {
      label: 'Additional due to price increase',
      color: 'var(--spend-600)',
    },
  } satisfies ChartConfig;

  return (
    <DashboardSection
      title="Overall spend"
      description="Understand your overall spend on this item."
    >
      <DashboardGrid>
        <MetricStack>
          <MetricCard
            title="Total spend"
            value={
              totalSpend != null
                ? withCurrency(Math.trunc(totalSpend).toLocaleString('en-GB'))
                : '—'
            }
            description={periodLabel}
          />
          <MetricCard
            title="Average monthly spend"
            value={
              averageMonthlySpend != null
                ? withCurrency(
                    Math.round(averageMonthlySpend).toLocaleString('en-GB')
                  )
                : '—'
            }
            description={`Across ${spend.availableMonthTimePeriods} months with purchases`}
          />

          <MetricCard
            title="Share of overall spend"
            value={proportion != null ? (proportion * 100).toFixed(1) : '—'}
            unit={proportion != null ? '%' : undefined}
            description={
              totalSpend != null && overallSpend != null
                ? `${formatCompact(totalSpend, symbol)} of ${formatCompact(
                    overallSpend,
                    symbol
                  )} total ${categoryLabel ? `${categoryLabel} ` : ''}spend`
                : undefined
            }
          />
        </MetricStack>

        <ChartCard
          title="Spend per month"
          className="lg:col-span-2"
          legend={
            <>
              <ChartLegend
                name={chartConfig.baseline.label}
                color={chartConfig.baseline.color}
              />
              <ChartLegend
                name={chartConfig.additional.label}
                color={chartConfig.additional.color}
              />
            </>
          }
        >
          <SimpleBarChart
            chartConfig={chartConfig}
            chartData={chartData}
            className="aspect-auto h-full w-full"
            margin={{ top: 20, right: 10, left: 0, bottom: 40 }}
            nameKey="period"
            dataKey="baseline"
            stacked
            topRadius={4}
            XAxisProps={{
              // Cap at ~8 evenly spaced labels so long windows stay legible.
              interval: Math.max(0, Math.ceil(chartData.length / 8) - 1),
              angle: -30,
              textAnchor: 'end',
              tickMargin: 2,
              dx: 10,
              tickFormatter: (value: string) => truncateLabel(value, 15),
            }}
            YAxisProps={{
              tickFormatter: (value: number) => formatCompact(value, symbol),
              width: 56,
            }}
            tooltipValueFormatter={(value) =>
              typeof value === 'number' ? formatCompact(value, symbol) : value
            }
          />
        </ChartCard>
      </DashboardGrid>
    </DashboardSection>
  );
}
